import * as React from "react";
import { Form, Checkbox, useFormState, useFormApi } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useParams, Link } from "react-router-dom";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { StudyPcfSetting } from "../../../misc/api/apiTypes";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

interface PcfSettingsScreenFormState {
    maximumTests: number;
    selectedTestParameters: ISelectListItem[];
    testAcceptanceChange: boolean;
    testAcceptanceChangeWindow: number;
}

interface PcfSettingsScreenFormProps {
    studyId: string;
    parameters: any[];
}

export const PcfSettingsScreenForm = ({ studyId, parameters }: PcfSettingsScreenFormProps) => {
    const formApi = useFormApi<PcfSettingsScreenFormState>();
    const formState = useFormState<PcfSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const convertToIdsArray = (params: ISelectListItem[]) => {
        return params && params.length > 0
            ? parameters.filter(p => params.map(s => s.Value).includes(p.Keyword)).map(p => p.Id)
            : null;
    }

    const onPreAuditSubmit = () => {
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const PcfSettings: StudyPcfSetting = {
            SelectedTestParameters: convertToIdsArray(state.values.selectedTestParameters),
            MaxTests: state.values.maximumTests,
            TestAcceptanceChange: state.values.testAcceptanceChange,
            TestAcceptanceChangeTimeWindow: state.values.testAcceptanceChangeWindow,
        }

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Pcf.StudyPcfSetting", PcfSettings);

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    }

    const maximumTestValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 4, 8);
    }

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false
        };
    });

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">PCF Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Maximum Tests</label>
                                    <p className="help-block">Configures the maximum number of tests allowed</p>
                                    <TextBox<number, PcfSettingsScreenFormState> id="maximumTests" field="maximumTests" type="number" min="4" max="8" required validate={maximumTestValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens for supported applications</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={parametersSelectList}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Test Acceptance Change Allowed</label>
                                    <p className="help-block">Configures whether test accceptance changes are allowed and the window of time after session completion</p>
                                    <Checkbox<boolean, PcfSettingsScreenFormState> field="testAcceptanceChange" />
                                    <TextBoxWithStaticSuffix<number, PcfSettingsScreenFormState> field="testAcceptanceChangeWindow" type="number" min="0" max="120" staticpart="Hours" disabled={!formState.values.testAcceptanceChange} validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 0, 120)} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
                {auditModalVisible ? (
                    <AuditModal
                        header="Approval"
                        isSubmitting={submitting}
                        onClose={() => setAuditModalVisible(false)}
                        onSubmit={(audit) => onSubmit(audit)}
                    />
                ) : null}
            </div>
        </>
    );
}

export const PcfSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<PcfSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const pcfParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Pcf);
            setParameters(pcfParametersResult);

            const pcfSettings = await legacyClient.getStudyPcfSettings(id);

            setInitialValues({
                maximumTests: pcfSettings.MaxTests,
                testAcceptanceChange: pcfSettings.TestAcceptanceChange,
                testAcceptanceChangeWindow: pcfSettings.TestAcceptanceChangeTimeWindow,
                selectedTestParameters: ConvertToSelectListItemArray(pcfSettings.SelectedTestParameters, pcfParametersResult),
            });
        }

        getSettings();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading PCF Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form noValidate initialValues={initialValues} validateFields={validateFields}>
            <PcfSettingsScreenForm studyId={id} parameters={parameters} />
        </Form>
    );
}
